'use client';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  CONSENT_COOKIE, CONSENT_VERSION, parseConsent, serializeConsent, consentModeUpdate,
  type ConsentChoice, type ConsentRecord,
} from './consent.helpers';
import { useGeo } from './GeoProvider';
import { regionFromCountry, type ConsentRegion } from './consentRegion';

type BannerMode = 'modal' | 'notice' | null;

interface ConsentContextValue {
  record: ConsentRecord | null;   // effective consent (explicit choice, or region default)
  region: ConsentRegion;
  showBanner: boolean;
  bannerMode: BannerMode;         // 'modal' (strict) | 'notice' (open) | null
  openPreferences: () => void;
  save: (choice: ConsentChoice) => void;
}
const ConsentContext = createContext<ConsentContextValue>({
  record: null, region: 'open', showBanner: false, bannerMode: null, openPreferences: () => {}, save: () => {},
});
export const useConsent = () => useContext(ConsentContext);

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  return document.cookie.split('; ').find((c) => c.startsWith(name + '='))?.split('=').slice(1).join('=');
}

function pushConsentMode(choice: ConsentChoice) {
  const g = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag;
  if (typeof g === 'function') g('consent', 'update', consentModeUpdate(choice));
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const { countryCode, loading } = useGeo();
  const [explicit, setExplicit] = useState<ConsentRecord | null>(null); // the visitor's stored choice
  const [hydrated, setHydrated] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);

  useEffect(() => {
    setExplicit(parseConsent(readCookie(CONSENT_COOKIE)));
    setHydrated(true);
  }, []);

  const region = regionFromCountry(countryCode);
  const geoResolved = hydrated && !loading;
  // A stale-version choice is treated as "no choice" so we re-ask on version bumps.
  const validExplicit = explicit && explicit.v === CONSENT_VERSION ? explicit : null;

  // Effective consent: explicit choice wins; else open regions default to granted
  // once geo has resolved; strict regions (or while geo is pending) stay denied.
  const record: ConsentRecord | null =
    validExplicit ??
    (geoResolved && region === 'open'
      ? { v: CONSENT_VERSION, analytics: true, marketing: true, ts: 0 }
      : null);

  // Show the prompt only when there's no valid choice yet and we know the region.
  const needsChoice = geoResolved && !validExplicit;
  const bannerMode: BannerMode = forceOpen
    ? 'modal'
    : needsChoice
      ? (region === 'strict' ? 'modal' : 'notice')
      : null;

  // Keep Google Consent Mode in sync with the effective record (e.g. auto-grant
  // for open regions). Denied is the default from the <head> script, so nulls need
  // no push.
  useEffect(() => {
    if (record) pushConsentMode({ analytics: record.analytics, marketing: record.marketing });
  }, [record?.analytics, record?.marketing]);

  const save = (choice: ConsentChoice) => {
    const value = serializeConsent(choice);
    document.cookie = `${CONSENT_COOKIE}=${value};path=/;max-age=${60 * 60 * 24 * 180};SameSite=Lax`;
    setExplicit(parseConsent(value));
    setForceOpen(false);
    pushConsentMode(choice);
  };

  return (
    <ConsentContext.Provider
      value={{ record, region, showBanner: bannerMode !== null, bannerMode, openPreferences: () => setForceOpen(true), save }}
    >
      {children}
    </ConsentContext.Provider>
  );
}
